/**
 * The board's OWN placement state. The shared panel's equivalents are closures
 * inside a 3600-line component (place `ChipPlacementPanel.tsx:1254-1514`,
 * clear/undo/double/repeat `:1559-1797`) and are not extractable without editing
 * it, so this is a slim re-implementation of exactly the behaviour Birdie needs
 * — the CrashChipPanel precedent, which imports nothing from the shared folder.
 *
 * Pure: every guard takes its limits as an argument and every action returns a
 * new state plus an optional notice key. No sounds, no timers, no store.
 *
 * Money is integer wire units throughout (the caller converts with
 * `fromInteger(value, currency)` before building `ChipLimits`); a cap of `0` in
 * the game config means "no limit" and reaches this module as
 * `Number.POSITIVE_INFINITY` (`ChipPlacementPanel.tsx:1010-1025`).
 */

import { PATTERNS } from 'src/lib/fairness/birdie'

export interface BirdieChip {
  optionId: string
  marketId: string
  amount: number
  /** Stack depth — display only. */
  count: number
}

export type BirdieChips = Record<string, BirdieChip>

export interface BirdieChipState {
  chips: BirdieChips
  /** Undo stack: a snapshot taken BEFORE every mutating action. */
  history: BirdieChips[]
  /** The last board that went to the wire (or was cleared) — the Repeat source. */
  lastBets: BirdieChips | null
}

export const EMPTY_CHIP_STATE: BirdieChipState = { chips: {}, history: [], lastBets: null }
export const UNDO_DEPTH = 20

export type ChipNotice =
  | 'balance-loading'
  | 'option-max'
  | 'insufficient-balance'
  | 'partial-double'
  | 'double-maxed'
  | 'nothing-to-repeat'
  | 'repeat-clamped'
  /** The round carries no odds, so no option has a price to bet at (B0). */
  | 'round-unpriced'
  /**
   * The option's stake is a free bet, which IS the bet: a single inventory item
   * of a fixed value, which the backend cannot split or top up
   * (`ChipPlacementPanel.tsx:1261-1266`).
   */
  | 'free-bet-locked'

/** Structurally identical to `ClampedChip` in `birdieMarkets.ts` (B0), which
 * `repeatClampNotice` consumes to build the notice copy in B9. */
export interface ChipClamp {
  optionId: string
  from: number
  to: number
}

export interface ChipLimits {
  optionMax: ReadonlyMap<string, number>
  balance: number
  balanceLoaded: boolean
  bettableIds: ReadonlySet<string>
  /**
   * At least one option is priced AND bettable this round. False means
   * `race_ready` carried no odds (or none the widget could parse): the tiles
   * are drawn greyed and nothing may go to the wire, because the only
   * multipliers left would be the game-config TEMPLATE's, which Go never
   * prices from (B0).
   */
  roundPriced: boolean
  /**
   * The options whose chip is funded by a free bet (`birdieInventory.ts`). Three
   * rules hang off it, all of them the shared panel's: such an option takes no
   * further stake (`ChipPlacementPanel.tsx:1261-1266`), a ×2 passes it through
   * untouched (`:1614-1618`), and — Birdie's own, because only Birdie re-prices
   * every round — a carry-forward DROPS it rather than clamping it, since a free
   * bet is a fixed stake and may not be quietly cut down to fit a new ceiling.
   * Absent means no free bet is on the board, which is every cash-only round.
   */
  freeBetOptionIds?: ReadonlySet<string>
}

export type BirdieChipAction =
  | {
      type: 'place'
      optionId: string
      marketId: string
      amount: number
      /**
       * The stake is a free bet's, not the wallet's, so it is not checked
       * against the balance — the shared panel skips the same two guards on its
       * own free-bet path (`ChipPlacementPanel.tsx:1284-1288`). The per-option
       * ceiling still applies: `birdieInventory.plannedClaim` only offers a free
       * bet that fits it, so a refusal here means something else changed.
       */
      freeBet?: boolean
    }
  | { type: 'remove'; optionId: string }
  | { type: 'undo' }
  | { type: 'clear' }
  | { type: 'double' }
  | { type: 'repeat' }
  | { type: 'snapshot' }
  | { type: 'roundReset'; keep: boolean }
  | { type: 'restore'; chips: BirdieChips }
  /**
   * The server refused (or never registered) these options' bets. Distinct
   * from `remove`, which is the player's own ✕: this one also prunes
   * `lastBets` — the Repeat source is snapshotted BEFORE the wire push — and
   * clears the undo history so nothing can put a refused stake back on the
   * board.
   */
  | { type: 'rejected'; optionIds: readonly string[] }

export interface ChipOutcome {
  state: BirdieChipState
  notice?: ChipNotice
  clamped?: ChipClamp[]
}

/**
 * The notices that are a total refusal — the reducer hands back the state it
 * was given and not one chip changed. The other two are partial SUCCESSES:
 * `partial-double` grew what it could and `repeat-clamped` replayed the board
 * smaller, and both really moved it.
 */
const REFUSALS: readonly ChipNotice[] = [
  'round-unpriced',
  'balance-loading',
  'option-max',
  'insufficient-balance',
  'double-maxed',
  'nothing-to-repeat',
  'free-bet-locked'
]

/**
 * Did this outcome actually change the board. B32 asks because a hand on the
 * board turns auto bet off, and a tap the reducer refused is not a hand on the
 * board — the rule would stop for a placement that never happened, and the
 * refusal's own notice would bury the sentence saying so.
 */
export const boardMoved = (outcome: ChipOutcome): boolean =>
  outcome.notice === undefined || !REFUSALS.includes(outcome.notice)

export function totalStaked(chips: BirdieChips): number {
  let sum = 0
  for (const key of Object.keys(chips)) sum += chips[key]?.amount ?? 0
  return sum
}

export function chipCount(chips: BirdieChips): number {
  return Object.keys(chips).length
}

/**
 * Is there anything for Repeat to replay? The shared strip shows Repeat in the
 * right-hand top slot whenever the board is empty and this is true (B31), so
 * the button's enabled state and the `repeat` case's own first guard have to be
 * the same fact — an enabled button whose action can only answer
 * `nothing-to-repeat` is a lie the player has to click to discover.
 *
 * Not simply `lastBets !== null`, which is what the shared panel tests: only
 * a board that HAD chips is ever snapshotted (`clear` / `snapshot`), but
 * `rejected` prunes the snapshot in place and can empty it without nulling it.
 *
 * It deliberately does NOT ask whether the options are still bettable at this
 * round's odds. That answer belongs to `repeat` itself, which re-prices the
 * whole board against the live limits and reports what it had to clamp or drop;
 * predicting it here would need this round's `ChipLimits` and would still be a
 * guess the reducer then re-does.
 */
export function canRepeat(state: BirdieChipState): boolean {
  return state.lastBets !== null && chipCount(state.lastBets) > 0
}

/** Canonical COUNT key. Deliberately group-free: the build targets es5. */
const IN_KEY_RE = /^IN[0-3]$/

const holesIn = (pattern: string): number => {
  let made = 0
  for (let i = 0; i < pattern.length; i++) if (pattern.charAt(i) === 'H') made++
  return made
}

/**
 * Does a chip on the canonical key `key` get paid when the round settles on
 * `pattern`? A COUNT key wins on the NUMBER of holed putts ('IN2' on any
 * two-hole pattern); an EXACT key wins only on itself.
 */
export function optionWins(key: string, pattern: string): boolean {
  if (IN_KEY_RE.test(key)) return holesIn(pattern) === Number(key.slice(2))
  return key === pattern
}

/** The B36 round-type keys. One type per round, so a FROST chip and a FIRE chip can never both pay. */
const isRoundTypeKey = (key: string): boolean => key === 'FROST' || key === 'FIRE'

/**
 * The most this board can pay: the best of the EIGHT settled patterns, not the
 * best chip per market summed. Both putt markets are decided by the SAME three
 * putts, so their winners are correlated — IN0 pays only on MMM, which is
 * exactly the pattern on which every EXACT chip except MMM loses. Summing a
 * best-per-market (the shared panel's model, `ChipPlacementPanel.tsx:1045-1119`,
 * where the markets really are independent) over-reports the number the player
 * is shown before committing.
 *
 * The ROUND-TYPE market (B36) is the exception: the type is drawn from the seed,
 * not from the putts, so it IS independent of the pattern and is added on top of
 * the best one. FROST and FIRE are exclusive, so the better of the two counts.
 * Before this term existed every FROST/FIRE chip was quoted at 0 — `optionWins`
 * knows only patterns — and the player was paid ~10× what the pill promised.
 *
 * A chip whose option has no known multiplier OR no known key is skipped, not
 * counted as 0x: the odds arrive with `race_ready`, and a board restored before
 * them must not report a potential win of zero.
 */
export function totalPotentialWin(
  chips: BirdieChips,
  multiplierById: ReadonlyMap<string, number>,
  keyById: Readonly<Record<string, string>>
): number {
  let best = 0
  for (const pattern of PATTERNS) {
    let sum = 0
    for (const id of Object.keys(chips)) {
      const chip = chips[id]
      if (!chip) continue
      const multiplier = multiplierById.get(chip.optionId)
      if (multiplier === undefined) continue
      const key = keyById[chip.optionId]
      if (key === undefined || !optionWins(key, pattern)) continue
      sum += chip.amount * multiplier
    }
    if (sum > best) best = sum
  }
  let frost = 0
  let fire = 0
  for (const id of Object.keys(chips)) {
    const chip = chips[id]
    if (!chip) continue
    const key = keyById[chip.optionId]
    if (key === undefined || !isRoundTypeKey(key)) continue
    const multiplier = multiplierById.get(chip.optionId)
    if (multiplier === undefined) continue
    if (key === 'FROST') frost += chip.amount * multiplier
    else fire += chip.amount * multiplier
  }
  return best + Math.max(frost, fire)
}

/**
 * The caps recipe, copied (never imported) from `ChipPlacementPanel.tsx:1027-1040`:
 * `min(maxBetAmount, pickWinnerLimit, maxPayout / multiplier)`. A zero or absent
 * multiplier is unbounded by the payout ceiling rather than a division by zero.
 */
export function optionMaxBets(
  options: readonly { id: string; multiplier: number }[],
  caps: { maxBetAmount: number; pickWinnerLimit: number; maxPayout: number }
): Map<string, number> {
  const result = new Map<string, number>()
  for (const option of options) {
    const fromPayout =
      option.multiplier > 0 ? caps.maxPayout / option.multiplier : Number.POSITIVE_INFINITY
    result.set(option.id, Math.min(caps.maxBetAmount, caps.pickWinnerLimit, fromPayout))
  }
  return result
}

const pushHistory = (state: BirdieChipState): BirdieChips[] => {
  const next = state.history.concat([{ ...state.chips }])
  return next.length > UNDO_DEPTH ? next.slice(next.length - UNDO_DEPTH) : next
}

interface Revalidated {
  chips: BirdieChips
  /** Chips cut back to this round's per-option ceiling. */
  clamped: ChipClamp[]
  /** Chips removed outright — this round does not offer the option (`to: 0`). */
  dropped: ChipClamp[]
  total: number
}

/**
 * Re-prices a board against THIS round's limits. Birdie quotes new odds every
 * round, so both the per-option ceiling (`maxPayout / multiplier`) and the set
 * of bettable options move — a board built under last round's numbers is not
 * automatically legal under this round's.
 *
 * Shared by `repeat` (replaying `lastBets`) and by a `roundReset` that KEEPS
 * the chips: the two differ only in how they report the result.
 */
function revalidate(source: BirdieChips, limits: ChipLimits): Revalidated {
  const chips: BirdieChips = {}
  const clamped: ChipClamp[] = []
  const dropped: ChipClamp[] = []
  let total = 0
  for (const key of Object.keys(source)) {
    const chip = source[key]
    if (!chip) continue
    const max = limits.optionMax.get(chip.optionId) ?? Number.POSITIVE_INFINITY
    const amount = limits.bettableIds.has(chip.optionId) ? Math.min(chip.amount, max) : 0
    // A free bet is a FIXED stake, so this round's lower ceiling cannot cut it
    // down to fit — a clamp would spend the whole item on a smaller bet than the
    // reward is worth. It is dropped instead, which releases it back into the
    // pool for an option that can take it (`birdieInventory.plannedClaim`).
    const freeFunded = limits.freeBetOptionIds?.has(chip.optionId) === true
    if (amount <= 0 || (freeFunded && amount < chip.amount)) {
      dropped.push({ optionId: chip.optionId, from: chip.amount, to: 0 })
      continue
    }
    if (amount < chip.amount)
      clamped.push({ optionId: chip.optionId, from: chip.amount, to: amount })
    chips[key] = { ...chip, amount }
    total += amount
  }
  return { chips, clamped, dropped, total }
}

const without = (chips: BirdieChips, drop: ReadonlySet<string>): BirdieChips => {
  const next: BirdieChips = {}
  for (const key of Object.keys(chips)) {
    const chip = chips[key]
    if (!chip || drop.has(chip.optionId)) continue
    next[key] = chip
  }
  return next
}

export function birdieChipReducer(
  state: BirdieChipState,
  action: BirdieChipAction,
  limits: ChipLimits
): ChipOutcome {
  switch (action.type) {
    case 'place': {
      if (!limits.roundPriced) return { state, notice: 'round-unpriced' }
      if (!limits.balanceLoaded) return { state, notice: 'balance-loading' }
      if (limits.freeBetOptionIds?.has(action.optionId) === true) {
        return { state, notice: 'free-bet-locked' }
      }
      const current = state.chips[action.optionId]
      const onOption = (current?.amount ?? 0) + action.amount
      const max = limits.optionMax.get(action.optionId) ?? Number.POSITIVE_INFINITY
      if (onOption > max) return { state, notice: 'option-max' }
      if (!action.freeBet && totalStaked(state.chips) + action.amount > limits.balance) {
        return { state, notice: 'insufficient-balance' }
      }
      return {
        state: {
          ...state,
          history: pushHistory(state),
          chips: {
            ...state.chips,
            [action.optionId]: {
              optionId: action.optionId,
              marketId: action.marketId,
              amount: onOption,
              count: (current?.count ?? 0) + 1
            }
          }
        }
      }
    }

    case 'remove': {
      if (!state.chips[action.optionId]) return { state }
      // Rebuilt rather than spread-and-`delete`: keeps insertion order (the
      // board renders in placement order) and keeps the object a clean literal.
      const chips: BirdieChips = {}
      for (const key of Object.keys(state.chips)) {
        if (key === action.optionId) continue
        const chip = state.chips[key]
        if (chip) chips[key] = chip
      }
      return { state: { ...state, history: pushHistory(state), chips } }
    }

    case 'undo': {
      const previous = state.history[state.history.length - 1]
      if (!previous) return { state }
      return { state: { ...state, chips: previous, history: state.history.slice(0, -1) } }
    }

    case 'clear': {
      const hadChips = chipCount(state.chips) > 0
      return {
        state: {
          chips: {},
          history: [],
          lastBets: hadChips ? state.chips : state.lastBets
        }
      }
    }

    /**
     * Every chip goes to `min(amount * 2, per-option max)`, and a chip whose
     * full growth does not fit the balance is left alone rather than grown
     * half-way. That is the one deliberate divergence from the shared panel,
     * which clips growth to the remaining budget
     * (`ChipPlacementPanel.tsx:1633-1637`): this reducer's notice vocabulary has
     * no "grown, but not all the way, because of your balance" key, so a
     * half-grown chip would be reported as a clean double. All-or-nothing keeps
     * `double-maxed` truthful ("nothing moved") and `partial-double` truthful
     * ("something did not reach 2x"), whatever clipped it.
     */
    case 'double': {
      if (!limits.roundPriced) return { state, notice: 'round-unpriced' }
      const keys = Object.keys(state.chips)
      if (keys.length === 0) return { state }
      // Chips not yet visited keep their CURRENT stake reserved, so an early
      // chip's growth can never price a later chip's existing bet off the board.
      let unvisited = totalStaked(state.chips)
      let committed = 0
      let clampedAny = false
      let changedAny = false
      const chips: BirdieChips = {}
      for (const key of keys) {
        const chip = state.chips[key]
        if (!chip) continue
        unvisited -= chip.amount
        // A free bet is one inventory item of one fixed value: there is no
        // second one to add, so the chip rides the ×2 through unchanged and
        // reports neither growth nor a clamp (`ChipPlacementPanel.tsx:1614-1618`).
        if (limits.freeBetOptionIds?.has(chip.optionId) === true) {
          committed += chip.amount
          chips[key] = chip
          continue
        }
        const max = limits.optionMax.get(chip.optionId) ?? Number.POSITIVE_INFINITY
        const target = Math.max(chip.amount, Math.min(chip.amount * 2, max))
        const affordable = committed + target + unvisited <= limits.balance
        const amount = affordable ? target : chip.amount
        if (amount < chip.amount * 2) clampedAny = true
        if (amount > chip.amount) changedAny = true
        committed += amount
        chips[key] = { ...chip, amount, count: amount > chip.amount ? chip.count * 2 : chip.count }
      }
      if (!changedAny) return { state, notice: 'double-maxed' }
      return {
        state: { ...state, history: pushHistory(state), chips },
        notice: clampedAny ? 'partial-double' : undefined
      }
    }

    case 'repeat': {
      if (!limits.roundPriced) return { state, notice: 'round-unpriced' }
      const source = state.lastBets
      if (!source || chipCount(source) === 0) return { state, notice: 'nothing-to-repeat' }
      // An option this round does not price is dropped, not clamped: the board
      // only offers what `applyRoundOdds` marked bettable (B0).
      const { chips, clamped, total } = revalidate(source, limits)
      if (chipCount(chips) === 0) return { state, notice: 'nothing-to-repeat' }
      if (total > limits.balance) return { state, notice: 'insufficient-balance' }
      return {
        state: { ...state, history: pushHistory(state), chips },
        notice: clamped.length > 0 ? 'repeat-clamped' : undefined,
        clamped: clamped.length > 0 ? clamped : undefined
      }
    }

    case 'snapshot': {
      if (chipCount(state.chips) === 0) return { state }
      return { state: { ...state, lastBets: { ...state.chips } } }
    }

    /**
     * `keep` is the carry-forward case: nothing was submitted last round, so
     * the chips on the board are the player's INTENT for this one and survive.
     * They are still re-priced against this round's board — Birdie's odds (and
     * therefore the per-option ceiling and the bettable set) are new every
     * round, so a chip that was legal last round can be over the cap or on an
     * option this round does not offer. Sending it as placed would put the
     * stake on at a price the player never saw.
     *
     * Drops are reported alongside the clamps: `repeat-clamped`'s copy ("N
     * chips reduced to this round's max") covers both, and a dropped chip is
     * exactly one reduced to a max of nothing.
     */
    case 'roundReset': {
      if (!action.keep) return { state: { chips: {}, history: [], lastBets: state.lastBets } }
      if (chipCount(state.chips) === 0) return { state }
      const { chips, clamped, dropped } = revalidate(state.chips, limits)
      const cuts = clamped.concat(dropped)
      if (cuts.length === 0) return { state }
      return {
        // History goes with the board it described — an undo must not restore
        // a stake this round's limits just refused.
        state: { ...state, history: [], chips },
        notice: 'repeat-clamped',
        clamped: cuts
      }
    }

    case 'restore':
      return { state: { ...state, chips: action.chips, history: [] } }

    case 'rejected': {
      if (action.optionIds.length === 0) return { state }
      const drop = new Set(action.optionIds)
      const chips = without(state.chips, drop)
      const lastBets = state.lastBets ? without(state.lastBets, drop) : state.lastBets
      const changed =
        chipCount(chips) !== chipCount(state.chips) ||
        (state.lastBets !== null &&
          lastBets !== null &&
          chipCount(lastBets) !== chipCount(state.lastBets))
      if (!changed) return { state }
      return { state: { ...state, chips, lastBets, history: [] } }
    }

    default:
      return { state }
  }
}
